import * as Yup from 'yup';

export const validationSchemaAccount = Yup.object().shape({
  name: Yup.string().required('Account Name is required'),

  phone: Yup.string()
    .nullable()
    .trim()
    .test(
      'is-valid-phone',
      'Phone number must be 7-15 digits with optional separators (+, -, (, ), space)',
      (value) => {
        if (!value) return true; // Allow empty

        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length < 7 || digitsOnly.length > 15) return false;

        // Check allowed characters only
        if (!/^[0-9+\-\s()]+$/.test(value)) return false;

        // Reject if two special characters (non-digits) come back-to-back (excluding spaces)
        if (/(?! )[\+\-\(\)]{2,}/.test(value)) return false;

        return true;
      }
    ),

  alternatePhone: Yup.string()
    .nullable()
    .trim()
    .test(
      'is-valid-phone',
      'Phone number must be 7-15 digits with optional separators (+, -, (, ), space)',
      (value) => {
        if (!value) return true; // Allow empty

        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length < 7 || digitsOnly.length > 15) return false;

        // Check allowed characters only
        if (!/^[0-9+\-\s()]+$/.test(value)) return false;

        // Reject if two special characters (non-digits) come back-to-back (excluding spaces)
        if (/(?! )[\+\-\(\)]{2,}/.test(value)) return false;

        return true;
      }
    ),

  companyLinkedIn: Yup.string()
    .trim()
    .nullable()
    .test('is-valid-linkedin', 'LinkedIn URL must be a valid LinkedIn profile URL', (value) => {
      if (!value) return true;
      // Convert to lowercase for case-insensitive validation
      const lowerValue = value.toLowerCase();
      return /^(https?:\/\/)?(www\.)?linkedin\.com\/[\w@.-]+.*$/.test(lowerValue);
    }),

  website: Yup.string()
    .trim()
    .max(200, 'URL cannot exceed 200 characters')
    .nullable()
    .test('is-valid-url', 'Invalid website format', (value) => {
      if (!value) return true; // Skip validation if value is empty

      // Convert to lowercase for case-insensitive validation
      const lowerValue = value.toLowerCase();

      // Basic URL format check
      const urlRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/;
      if (!urlRegex.test(lowerValue)) return false;

      // Valid top-level domains
      const validTLDs = [
        'com',
        'org',
        'net',
        'edu',
        'gov',
        'mil',
        'int',
        'info',
        'biz',
        'name',
        'pro',
        'museum',
        'coop',
        'aero',
        'xxx',
        'idv',
        'mobi',
        'asia',
        'tel',
        'jobs',
        'travel',
        'in',
        'us',
        'uk',
        'ca',
        'au',
        'de',
        'fr',
        'jp',
        'io',
        'ai',
        'co',
      ];

      // Extract domain and check TLD
      const domain = lowerValue.replace(/^(https?:\/\/)?(www\.)?/, '');
      const tld = domain.split('/')[0].split('.').pop();
      return validTLDs.includes(tld);
    }),

  city: Yup.string()
    .nullable()
    .trim() // Automatically trim leading and trailing spaces
    .max(50, 'City cannot exceed 50 characters.')
    .test('valid-characters', 'Please enter valid city', (value) => {
      if (!value) return true; // Allow blank input
      return /^[A-Za-z\s'-.]+$/.test(value);
    }),

  state: Yup.string()
    .nullable()
    .test('is-alphabet-only', 'State should only contain alphabets', (value) => {
      if (!value) return true; // Allow blank input
      return /^[A-Za-z\s]+$/.test(value); // Only alphabets if not blank
    }),

  country: Yup.string()
    .nullable()
    .trim()
    .test('valid-country', 'Country should only contain alphabets and hyphens', (value) => {
      if (!value) return true; // Allow blank input
      // Allow alphabets, spaces and single hyphens
      return (
        /^[A-Za-z\s-]+$/.test(value) && // Only alphabets, spaces and hyphens
        !/--/.test(value) && // No consecutive hyphens
        !/^-|-$/.test(value)
      ); // No leading/trailing hyphens
    }),

  zipCode: Yup.string()
    .nullable()
    .trim()
    .test(
      'is-valid-zip-code',
      'ZIP code must be 3-10 characters with only letters, numbers and hyphens',
      (value) => {
        if (!value) return true; // Allow empty value

        // Check for valid characters and length
        return (
          /^[A-Za-z0-9-]{3,10}$/.test(value) && // Only letters, numbers, hyphen
          !/--/.test(value)
        ); // No consecutive hyphens
      }
    ),
});
